import React, { useMemo } from 'react';
import { OPTIONAL_COLUMNS, computeSectionTotals } from './templateSchema';

/**
 * Read-only preview of the template structure. Shown in a modal when the user
 * clicks "Preview" in the editor.
 *
 * Layout:
 *   1. Meta header   — Template Name, Max Marks, Duration, Instructions
 *   2. Question paper table
 *   3. Statistical analysis — counts & marks distribution per visible attribute
 *      (CO, Unit, BL, Type, Difficulty, KP, PO), driven by the column-visibility
 *      setting so instructors can verify coverage balance at a glance.
 */
function TemplatePreviewModal({ template, structure, visibleColumns, onClose }) {
  const visibleCols = OPTIONAL_COLUMNS.filter((c) => visibleColumns.includes(c.key));

  // -------------------- Question rendering --------------------
  const renderQuestion = (q, depth = 0) => (
    <React.Fragment key={q.id}>
      <tr>
        <td style={{ paddingLeft: 16 + depth * 20 }}>{q.number || '—'}</td>
        <td>{q.marks || 0}</td>
        {visibleCols.map((c) => (
          <td key={c.key}>{q[c.key] || '—'}</td>
        ))}
      </tr>
      {q.subQuestions?.map((sq) => renderQuestion(sq, depth + 1))}
      {q.connectorAfter && (
        <tr className={`qp-preview-connector qp-preview-connector-${q.connectorAfter.type.toLowerCase()}`}>
          <td colSpan={2 + visibleCols.length} className="qp-preview-connector-cell">
            — {q.connectorAfter.type} —
          </td>
        </tr>
      )}
    </React.Fragment>
  );

  // -------------------- Distribution stats --------------------
  // Count only leaf questions (questions without sub-questions). Parents are
  // containers — their marks are expressed through the sub-questions that
  // students actually answer, so counting both would double-count.
  const distributions = useMemo(() => {
    const flatLeaves = [];
    const walk = (qs) => {
      for (const q of qs) {
        if (q.subQuestions?.length) walk(q.subQuestions);
        else flatLeaves.push(q);
      }
    };
    walk(structure.questions || []);
    for (const sec of structure.sections || []) walk(sec.questions || []);

    const totalMarks = flatLeaves.reduce((a, q) => a + (Number(q.marks) || 0), 0);
    const totalCount = flatLeaves.length;

    const out = {};
    for (const col of visibleCols) {
      const buckets = new Map();
      for (const q of flatLeaves) {
        const raw = q[col.key];
        const key = raw && String(raw).trim() !== '' ? String(raw) : '— Unassigned —';
        if (!buckets.has(key)) buckets.set(key, { count: 0, marks: 0 });
        const b = buckets.get(key);
        b.count += 1;
        b.marks += Number(q.marks) || 0;
      }
      // Sort: "Unassigned" always last, otherwise by marks desc, then value asc.
      const rows = Array.from(buckets.entries())
        .map(([value, stats]) => ({
          value,
          count: stats.count,
          marks: Math.round(stats.marks * 100) / 100,
          countPct: totalCount > 0 ? Math.round((stats.count / totalCount) * 100) : 0,
          marksPct: totalMarks > 0 ? Math.round((stats.marks / totalMarks) * 100) : 0,
          isUnassigned: value === '— Unassigned —',
        }))
        .sort((a, b) => {
          if (a.isUnassigned && !b.isUnassigned) return 1;
          if (!a.isUnassigned && b.isUnassigned) return -1;
          if (b.marks !== a.marks) return b.marks - a.marks;
          return a.value.localeCompare(b.value);
        });
      out[col.key] = { rows, totalCount, totalMarks };
    }
    return out;
  }, [structure, visibleCols]);

  const durationStr = template.durationMinutes
    ? `${template.durationMinutes} min`
    : '—';
  const maxMarksStr = template.maxMarks ? String(template.maxMarks) : '—';

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content qp-preview-modal" onClick={(e) => e.stopPropagation()}>
        <div className="qp-preview-header">
          <h3 style={{ margin: 0 }}>Preview</h3>
          <button className="btn btn-close" onClick={onClose}>×</button>
        </div>

        {/* Meta header: name / max marks / duration / instructions */}
        <div className="qp-preview-meta">
          <div className="qp-preview-meta-title">
            {template.templateName || 'Untitled Template'}
          </div>
          <div className="qp-preview-meta-pills">
            <span className="qp-preview-meta-pill">
              <span className="qp-preview-meta-pill-label">Max Marks</span>
              <span className="qp-preview-meta-pill-value">{maxMarksStr}</span>
            </span>
            <span className="qp-preview-meta-pill">
              <span className="qp-preview-meta-pill-label">Duration</span>
              <span className="qp-preview-meta-pill-value">{durationStr}</span>
            </span>
          </div>
          {template.instructions && (
            <div className="qp-preview-meta-instructions">
              <span className="qp-preview-meta-instructions-label">
                Instructions to Candidates
              </span>
              <p>{template.instructions}</p>
            </div>
          )}
        </div>

        <div className="qp-preview-body">
          {/* Question paper */}
          <div className="qp-preview-section-title">Question Paper</div>
          <table className="qp-preview-table">
            <thead>
              <tr>
                <th>Q.No</th>
                <th>Marks</th>
                {visibleCols.map((c) => (
                  <th key={c.key}>{c.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(structure.questions || []).length > 0 && (
                <React.Fragment>
                  <tr className="qp-preview-section-row">
                    <td colSpan={2 + visibleCols.length}>
                      <strong>Direct Questions</strong>
                      <span style={{ color: '#616161', marginLeft: 8 }}>
                        (no section)
                      </span>
                    </td>
                  </tr>
                  {(structure.questions || []).map((q) => renderQuestion(q))}
                </React.Fragment>
              )}

              {(structure.sections || []).map((sec) => {
                const secTotals = computeSectionTotals(sec);
                return (
                  <React.Fragment key={sec.id}>
                    <tr className="qp-preview-section-row">
                      <td colSpan={2 + visibleCols.length}>
                        <strong>{sec.label}</strong> {sec.name}
                        <span style={{ color: '#616161', marginLeft: 8 }}>
                          {secTotals.questionCount}/{sec.attemptRule?.total || secTotals.questionCount} · {secTotals.setMarks}/{secTotals.attemptMarks}m
                        </span>
                      </td>
                    </tr>
                    {(sec.questions || []).map((q) => renderQuestion(q))}
                  </React.Fragment>
                );
              })}

              {(structure.questions || []).length === 0 &&
                (!structure.sections || structure.sections.length === 0) && (
                  <tr>
                    <td colSpan={2 + visibleCols.length} style={{ textAlign: 'center', color: '#9e9e9e', padding: 24 }}>
                      No questions or sections defined yet.
                    </td>
                  </tr>
                )}
            </tbody>
          </table>

          {/* Statistical analysis */}
          {visibleCols.length > 0 && (
            <div className="qp-preview-stats-section">
              <div className="qp-preview-section-title">
                Statistical Analysis
                <span className="qp-preview-section-hint">
                  Distribution across {visibleCols.length}{' '}
                  {visibleCols.length === 1 ? 'attribute' : 'attributes'} ·
                  Based on {distributions[visibleCols[0].key]?.totalCount || 0}{' '}
                  leaf questions totalling{' '}
                  {distributions[visibleCols[0].key]?.totalMarks || 0} marks
                </span>
              </div>

              <div className="qp-preview-stats-grid">
                {visibleCols.map((col) => {
                  const d = distributions[col.key];
                  if (!d || d.rows.length === 0) {
                    return (
                      <div className="qp-preview-stats-card" key={col.key}>
                        <div className="qp-preview-stats-card-title">{col.fullLabel}</div>
                        <div className="qp-preview-stats-empty">No data</div>
                      </div>
                    );
                  }
                  return (
                    <div className="qp-preview-stats-card" key={col.key}>
                      <div className="qp-preview-stats-card-title">{col.fullLabel}</div>
                      <table className="qp-preview-stats-table">
                        <thead>
                          <tr>
                            <th>{col.label}</th>
                            <th style={{ textAlign: 'right' }}>Qs</th>
                            <th style={{ textAlign: 'right' }}>Marks</th>
                            <th>%</th>
                          </tr>
                        </thead>
                        <tbody>
                          {d.rows.map((r) => (
                            <tr
                              key={r.value}
                              className={r.isUnassigned ? 'qp-preview-stats-unassigned' : ''}
                            >
                              <td>{r.value}</td>
                              <td style={{ textAlign: 'right' }}>{r.count}</td>
                              <td style={{ textAlign: 'right' }}>{r.marks}</td>
                              <td>
                                <div className="qp-preview-stats-barwrap">
                                  <div
                                    className="qp-preview-stats-bar"
                                    style={{ width: `${Math.min(r.marksPct, 100)}%` }}
                                  />
                                  <span className="qp-preview-stats-pct">
                                    {r.marksPct}%
                                  </span>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default TemplatePreviewModal;
